import React, { useState, useEffect } from 'react';
import { 
    ArrowLeft, User, Phone, Mail, Scale, Activity, Pill, Home, MapPin, 
    Building, Flag, Package, FileText, PhoneIncoming, PhoneOutgoing, 
    Edit, Loader, X, CheckCircle, ChevronRight, Ruler, User2, Heart
} from 'lucide-react';
import { ordersApi } from '../services/api';
import { maskPhone, formatDate, formatCurrency } from '../utils/helpers';
import { useTheme } from '../context/ThemeContext';

function OrderDetails({ order, onClose }) {
  const { currentTheme } = useTheme();
  const isDark = currentTheme.textColor === '#FFFFFF';
  const [orderDetails, setOrderDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('callLogs');

  useEffect(() => {
    if (order && order.id) {
      fetchOrderDetails();
    }
  }, [order]);

  const fetchOrderDetails = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await ordersApi.getOrderDetails(order.id);
      setOrderDetails(data);
    } catch (error) {
      console.error('Error fetching order details:', error);
      setError('Failed to load order details. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const displayValue = (value) => {
    if (value === null || value === undefined || value === '' || value === 0) {
      return '-/-';
    }
    return value;
  };

  if (loading) {
    return (
      <div className={`flex items-center justify-center min-h-screen transition-colors duration-500 ${isDark ? 'bg-[#0F172A]' : 'bg-[#F8FAFC]'}`}>
        <div className="text-center">
          <Loader className="w-12 h-12 animate-spin mx-auto mb-4" style={{ color: currentTheme.color }} />
          <p className={isDark ? 'text-gray-400' : 'text-gray-600'}>Loading order details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`flex items-center justify-center min-h-screen transition-colors duration-500 ${isDark ? 'bg-[#0F172A]' : 'bg-[#F8FAFC]'}`}>
        <div className="text-center">
          <div className="w-16 h-16 bg-red-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <X className="w-8 h-8 text-red-500" />
          </div>
          <p className="text-red-500 font-bold mb-6">{error}</p>
          <button onClick={onClose} className="px-8 py-3 text-white rounded-2xl font-black uppercase tracking-widest transition-all active:scale-95 shadow-xl" style={{ backgroundColor: currentTheme.color }}>
            Go Back
          </button>
        </div>
      </div>
    );
  }

  const orderData = orderDetails?.order || order;
  const lead = orderDetails?.lead || {};
  const callLogs = orderDetails?.callLogs || [];
  const orderproduct = orderData?.orderItems || [];

  return (
    <div className={`min-h-screen p-6 font-sans transition-colors duration-500 ${isDark ? 'bg-[#0F172A]' : 'bg-[#F8FAFC]'}`}>
      
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-4">
          <button 
            onClick={onClose}
            className={`p-3 rounded-2xl transition-all shadow-lg active:scale-90 ${isDark ? 'bg-white/5 text-white hover:bg-white/10' : 'bg-white text-gray-700 hover:bg-gray-50'}`}>
            <ArrowLeft size={24} style={{ color: currentTheme.color }} />
          </button>
          <div>
            <h1 className={`text-2xl font-black uppercase tracking-[0.2em] ${isDark ? 'text-white' : 'text-gray-800'}`}>Order Details</h1>
            <p className={`text-[10px] font-black uppercase tracking-widest mt-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Management & Overview</p>
          </div>
        </div>
        <button className={`p-3 rounded-2xl transition-all shadow-lg active:scale-90 ${isDark ? 'bg-white/5 text-orange-400 hover:bg-white/10' : 'bg-orange-50 text-orange-500 hover:bg-orange-100'}`}>
          <Edit className="w-6 h-6" />
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 max-w-[1600px] mx-auto">
        {/* Left Column - Main Details */}
        <div className="lg:col-span-2 space-y-6">
          
          {/* Order Summary Ribbon */}
          <div className={`rounded-3xl overflow-hidden flex items-stretch shadow-2xl border transition-all ${isDark ? 'bg-[#111827] border-white/5' : 'bg-white border-gray-100'}`}>
            <div className="flex-1 grid grid-cols-3 p-8 gap-8">
              <div>
                <p className={`text-[10px] font-black uppercase tracking-widest mb-2 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Order ID</p>
                <p className={`text-xl font-black ${isDark ? 'text-white' : 'text-gray-900'}`}>{displayValue(orderData.orderId)}</p>
              </div>
              <div className={`border-l pl-8 ${isDark ? 'border-white/5' : 'border-gray-100'}`}>
                <p className={`text-[10px] font-black uppercase tracking-widest mb-2 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Order Date</p>
                <p className={`text-lg font-black ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{orderData.orderDate ? formatDate(orderData.orderDate) : '-/-'}</p>
              </div>
              <div className={`border-l pl-8 ${isDark ? 'border-white/5' : 'border-gray-100'}`}>
                <p className={`text-[10px] font-black uppercase tracking-widest mb-2 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Primary Product</p>
                <p className={`text-lg font-black truncate ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{orderproduct[0]?.productName || 'Product'}</p>
              </div>
            </div>
            <div className="px-10 flex flex-col justify-center items-center text-white min-w-[200px]" style={{ backgroundColor: currentTheme.color }}>
              <span className="text-[10px] font-black uppercase tracking-[0.2em] opacity-70 mb-1">Payable Amount</span>
              <span className="text-3xl font-black">{formatCurrency(Math.max(0, (orderData.grandTotal || 0) - (orderData.prepaidAmount || 0)))}</span>
            </div>
          </div>

          {/* Customer Details */}
          <div className={`p-8 rounded-3xl shadow-xl border transition-all ${isDark ? 'bg-[#111827] border-white/5' : 'bg-white border-gray-100'}`}>
            <h3 className={`text-sm font-black uppercase tracking-[0.2em] mb-8 ${isDark ? 'text-white' : 'text-gray-800'}`}>Customer Details</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              {[
                { icon: User, label: 'Customer name', value: displayValue(lead.customerName || orderData.customerName), color: 'text-blue-500' },
                { icon: Phone, label: 'Contact number', value: displayValue(lead.contactNumber || orderData.contactNumber), color: 'text-emerald-500' },
                { icon: Phone, label: 'Alternate number', value: displayValue(lead.alternateNumber), color: 'text-orange-400' }
              ].map((item, idx) => (
                <div key={idx} className={`p-4 rounded-2xl border flex items-center gap-4 transition-all ${isDark ? 'bg-white/5 border-white/5' : 'bg-gray-50 border-gray-100'}`}>
                  <item.icon className={`w-6 h-6 ${item.color}`} />
                  <div>
                    <p className={`text-[10px] font-black uppercase tracking-widest ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{item.label}</p>
                    <p className={`text-sm font-black mt-1 ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>{item.value}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { icon: Mail, label: 'Email', value: displayValue(lead.email), color: 'text-orange-500' },
                { icon: User2, label: 'Gender', value: displayValue(lead.gender), color: 'text-emerald-500' },
                { icon: User, label: 'Age', value: displayValue(lead.age), color: 'text-purple-500' },
                { icon: Ruler, label: 'Height', value: displayValue(lead.height), color: 'text-cyan-500' }
              ].map((item, idx) => (
                <div key={idx} className={`p-4 rounded-xl border flex flex-col gap-2 transition-all ${isDark ? 'bg-white/2 border-white/5' : 'bg-gray-50/50 border-gray-100'}`}>
                  <item.icon className={`w-4 h-4 ${item.color}`} />
                  <div>
                    <p className={`text-[10px] font-black uppercase tracking-widest ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{item.label}</p>
                    <p className={`text-xs font-black mt-1 truncate ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{item.value}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Basic Details */}
          <div className={`p-8 rounded-3xl shadow-xl border transition-all ${isDark ? 'bg-[#111827] border-white/5' : 'bg-white border-gray-100'}`}>
            <h3 className={`text-sm font-black uppercase tracking-[0.2em] mb-8 ${isDark ? 'text-white' : 'text-gray-800'}`}>Health Profile</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[
                  { icon: Scale, label: 'Weight', value: displayValue(lead.weight), color: 'text-red-400' },
                  { icon: Activity, label: 'Actual Problem', value: displayValue(lead.actualProblem), color: 'text-blue-400' },
                  { icon: Pill, label: 'Medical Treatment', value: displayValue(lead.existingMedicalTreatment), color: 'text-emerald-400' },
                  { icon: Heart, label: 'Marital Status', value: displayValue(lead.maritalStatus), color: 'text-purple-400' }
                ].map((item, idx) => (
                  <div key={idx} className={`p-5 rounded-2xl border transition-all ${isDark ? 'bg-white/5 border-white/5' : 'bg-gray-50 border-gray-100'}`}>
                    <item.icon className={`w-6 h-6 mb-3 ${item.color}`} />
                    <p className={`text-[10px] font-black uppercase tracking-widest ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{item.label}</p>
                    <p className={`text-sm font-black mt-1 ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>{item.value}</p>
                  </div>
                ))}
            </div>
          </div>

          {/* Address Section */}
          <div className={`p-8 rounded-3xl shadow-xl border transition-all ${isDark ? 'bg-[#111827] border-white/5' : 'bg-white border-gray-100'}`}>
            <h3 className={`text-sm font-black uppercase tracking-[0.2em] mb-6 ${isDark ? 'text-white' : 'text-gray-800'}`}>Delivery Address</h3>
            <div className={`p-6 rounded-2xl mb-8 flex items-start gap-4 border transition-all ${isDark ? 'bg-white/5 border-white/5' : 'bg-blue-50 border-blue-100'}`}>
               <MapPin className="w-8 h-8 mt-1 flex-shrink-0" style={{ color: currentTheme.color }} />
               <p className={`text-lg font-black tracking-wide ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{displayValue(lead.address)}</p>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
               {[
                 { label: 'Pin Code', value: displayValue(lead.areaPinCode || orderData.areaPinCode) },
                 { label: 'Post Office', value: displayValue(lead.cityTown || orderData.cityTown || lead.city || orderData.city) },
                 { label: 'District', value: displayValue(lead.district || orderData.district) },
                 { label: 'State', value: displayValue(lead.state || orderData.state) }
               ].map((item, idx) => (
                 <div key={idx}>
                    <p className={`text-[10px] font-black uppercase tracking-widest mb-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{item.label}</p>
                    <p className={`text-sm font-black ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>{item.value}</p>
                 </div>
               ))}
            </div>
          </div>

          {/* Call Logs & Orders Tabs */}
          <div className={`rounded-3xl shadow-xl border transition-all overflow-hidden ${isDark ? 'bg-[#111827] border-white/5' : 'bg-white border-gray-100'}`}>
            <div className={`flex border-b ${isDark ? 'border-white/5 bg-white/2' : 'border-gray-100 bg-gray-50/30'}`}>
              {['callLogs', 'orders'].map((tab) => (
                <button key={tab} onClick={() => setActiveTab(tab)} 
                  className={`px-8 py-5 text-[10px] font-black uppercase tracking-[0.2em] transition-all relative ${activeTab === tab ? (isDark ? 'text-white' : 'text-gray-900') : 'text-gray-400 hover:text-gray-500'}`}>
                  {tab === 'callLogs' ? 'Call logs' : 'Order history'}
                  {activeTab === tab && (
                    <div className="absolute bottom-0 left-0 right-0 h-1 rounded-t-full shadow-[0_-4px_10px_rgba(0,0,0,0.1)]" style={{ backgroundColor: currentTheme.color }} />
                  )}
                </button>
              ))}
            </div>

            <div className="p-8">
              {activeTab === 'callLogs' ? (
                <div className="space-y-4">
                  {callLogs.length > 0 ? (
                    callLogs.map((log, index) => (
                      <div key={index} className={`flex items-center justify-between p-4 rounded-2xl border transition-all ${isDark ? 'bg-white/2 border-white/5 hover:bg-white/5' : 'bg-gray-50 border-gray-100 hover:bg-white hover:shadow-md'}`}>
                        <div className="flex items-center gap-4">
                          <div className={`w-12 h-12 rounded-xl flex items-center justify-center border transition-all ${log.remarks?.toLowerCase().includes('incoming') ? (isDark ? 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30' : 'bg-emerald-50 text-emerald-600 border-emerald-100') : (isDark ? 'bg-blue-500/20 text-blue-400 border-blue-500/30' : 'bg-blue-50 text-blue-600 border-blue-100')}`}>
                            {log.remarks?.toLowerCase().includes('incoming') ? <PhoneIncoming size={20} /> : <PhoneOutgoing size={20} />}
                          </div>
                          <div>
                            <p className={`text-sm font-black ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>{log.contactNumber ? maskPhone(log.contactNumber) : '-/-'}</p>
                            <p className={`text-[10px] font-black uppercase tracking-widest ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>By, {displayValue(log.callerName)}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className={`text-xs font-black ${isDark ? 'text-gray-400' : 'text-gray-700'}`}>{log.callTime ? formatDate(log.callTime) : '-/-'}</p>
                          <p className={`text-[10px] font-black uppercase tracking-widest ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{displayValue(log.remarks)}</p>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="p-12 text-center opacity-40">
                      <Phone className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                      <p className="text-xs font-black uppercase tracking-widest">No call logs available</p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="p-12 text-center opacity-40">
                  <Package className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                  <p className="text-xs font-black uppercase tracking-widest">No previous orders found</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Right Column - Product Details & Summary */}
        <div className="space-y-6">
          {/* Product Details */}
          <div className={`p-8 rounded-3xl shadow-xl border transition-all ${isDark ? 'bg-[#111827] border-white/5' : 'bg-white border-gray-100'}`}>
            <h3 className={`text-sm font-black uppercase tracking-[0.2em] mb-6 ${isDark ? 'text-white' : 'text-gray-800'}`}>Product Selection</h3>

            {/* Product Card */}
            <div className={`flex items-center gap-4 p-4 rounded-2xl border mb-6 transition-all ${isDark ? 'bg-white/2 border-white/5 hover:bg-white/5' : 'bg-teal-50/50 border-teal-100 hover:bg-white hover:shadow-md'}`}>
              <div className={`w-14 h-14 rounded-xl flex items-center justify-center border transition-all ${isDark ? 'bg-teal-500/20 border-teal-500/30 text-teal-400' : 'bg-teal-500 text-white border-teal-600'}`}>
                <Package size={24} />
              </div>
              <div className="flex-1 min-w-0">
                <p className={`text-sm font-black truncate ${isDark ? 'text-gray-200' : 'text-gray-900'}`}>{displayValue(orderproduct[0]?.productName)}</p>
                <p className={`text-[10px] font-black uppercase tracking-widest mt-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Standard Selection</p>
              </div>
            </div>

            <div className={`flex justify-between items-center mb-8 pb-6 border-b transition-all ${isDark ? 'border-white/5' : 'border-gray-100'}`}>
              <span className={`text-[10px] font-black uppercase tracking-widest ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Quantity: 1</span>
              <span className={`text-2xl font-black ${isDark ? 'text-white' : 'text-gray-900'}`}>
                {orderData.grandTotal ? formatCurrency(orderData.grandTotal) : '₹0'}
              </span>
            </div>

            {/* Status Timeline-like Items */}
            <div className="space-y-6 mb-8">
              {[
                { label: 'Verified by', value: orderData.verifiedBy, icon: '✓', color: 'text-emerald-500' },
                { label: 'Dispatch by', value: orderData.dispatchBy, icon: '📦', color: 'text-blue-500' },
                { label: 'NDR by', value: orderData.ndrBy, icon: '↩️', color: 'text-orange-500' }
              ].map((step, idx) => (
                <div key={idx} className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className={`w-8 h-8 rounded-lg flex items-center justify-center text-xs border ${isDark ? 'bg-white/5 border-white/5' : 'bg-gray-50 border-gray-100'} ${step.color}`}>{step.icon}</span>
                    <span className={`text-[10px] font-black uppercase tracking-widest ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{step.label}</span>
                  </div>
                  <span className={`text-xs font-black ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{displayValue(step.value)}</span>
                </div>
              ))}
            </div>

            {/* Payment & Shipping Info */}
            <div className={`grid grid-cols-2 gap-4 pt-8 border-t transition-all ${isDark ? 'border-white/5' : 'border-gray-100'}`}>
              {[
                { label: 'Shipping', value: orderData.shippingMethod },
                { label: 'Payment Type', value: orderData.paymentType },
                { label: 'Payment Mode', value: orderData.paymentMode },
                { label: 'Prepaid', value: orderData.prepaidAmount ? formatCurrency(orderData.prepaidAmount) : '₹ 0', color: 'text-emerald-500' },
                { label: 'Discount', value: orderData.discount ? formatCurrency(orderData.discount) : '₹ 0', color: 'text-emerald-500' },
                { label: 'Approval', value: orderData.approvalStatus, isBadge: true }
              ].map((item, idx) => (
                <div key={idx} className={`p-4 rounded-2xl border transition-all ${isDark ? 'bg-white/2 border-white/5' : 'bg-gray-50/50 border-gray-100'}`}>
                  <p className={`text-[10px] font-black uppercase tracking-widest mb-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{item.label}</p>
                  {item.isBadge ? (
                    <span className={`inline-block px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest shadow-lg text-white`} style={{ backgroundColor: currentTheme.color }}>
                      {displayValue(item.value)}
                    </span>
                  ) : (
                    <p className={`text-xs font-black truncate ${item.color || (isDark ? 'text-gray-200' : 'text-gray-800')}`}>{displayValue(item.value)}</p>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Final Summary Card */}
          <div className={`p-8 rounded-3xl shadow-2xl border transition-all ${isDark ? 'bg-[#111827] border-white/5' : 'bg-white border-gray-100'}`}>
             <h3 className={`text-sm font-black uppercase tracking-[0.2em] mb-8 ${isDark ? 'text-white' : 'text-gray-800'}`}>Financial Summary</h3>
             <div className="space-y-4">
                {[
                  { label: 'Sub Total', value: orderData.subTotal ? formatCurrency(orderData.subTotal) : '₹0', color: isDark ? 'text-gray-200' : 'text-gray-900' },
                  { label: 'Prepaid Amount', value: orderData.prepaidAmount ? formatCurrency(orderData.prepaidAmount) : '₹0', color: 'text-emerald-500' },
                  { label: 'Total Discounts', value: orderData.totalDiscounts ? formatCurrency(orderData.totalDiscounts) : '₹0', color: 'text-emerald-500' },
                  { label: 'Shipping Charges', value: orderData.shippingCharges === 0 || !orderData.shippingCharges ? 'Free' : formatCurrency(orderData.shippingCharges), color: 'text-emerald-500' }
                ].map((item, idx) => (
                  <div key={idx} className="flex justify-between items-center">
                    <span className={`text-[10px] font-black uppercase tracking-widest ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{item.label}</span>
                    <span className={`text-sm font-black ${item.color}`}>{item.value}</span>
                  </div>
                ))}
                <div className="pt-6 border-t border-dashed mt-4" style={{ borderColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)' }}>
                  <div className="flex justify-between items-center">
                    <span className={`text-xs font-black uppercase tracking-[0.2em] ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Net Payable:</span>
                    <span className="text-3xl font-black" style={{ color: currentTheme.color }}>
                      {formatCurrency(Math.max(0, (orderData.grandTotal || 0) - (orderData.prepaidAmount || 0)))}
                    </span>
                  </div>
                </div>
             </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default OrderDetails;